import {
  Box, Grid, Stack, Typography, Card, CardActionArea, Radio, Slider,
  Divider, TextField, MenuItem, InputAdornment, Alert, Chip, Button, IconButton, Link,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateCredit, updateSecuredPostpaid, setCodSplit,
} from '../../../../redux/pricingWizardSlice';
import { useUploadCreditApprovalMutation } from '../../../../redux/wizardApiSlice';
import { ENGAGEMENT_MODELS } from '../constants';
import SectionTitle from './SectionTitle';

function classifyTier(gmv) {
  if (gmv < 500000) return { tier: 'Tier 1 · Small', color: '#475569' };
  if (gmv < 5000000) return { tier: 'Tier 2 · Medium', color: '#1e40af' };
  if (gmv < 50000000) return { tier: 'Tier 3 · Large', color: '#5b21b6' };
  return { tier: 'Tier 4 · Enterprise', color: '#166534' };
}

function computeRecommendedLimit({ engagement, gmv, credit_period_days, secured_postpaid, cod_share_pct }) {
  if (engagement === 'prepaid') return { value: 0, formula: 'N/A — prepaid model', note: 'No credit required for prepaid.' };
  if (engagement === 'postpaid') {
    const v = gmv * (credit_period_days / 30) * 1.2;
    return { value: Math.round(v), formula: `GMV × (${credit_period_days}/30) × 1.2`, note: 'Standard postpaid buffer.' };
  }
  if (engagement === 'secured_postpaid') {
    const w = secured_postpaid?.adjust_window_days || 7;
    const v = gmv * (w / 30) * (cod_share_pct / 100) * 1.1;
    return { value: Math.round(v), formula: `GMV × (${w}/30) × ${cod_share_pct}% × 1.1`, note: 'COD share boosts the limit — acts as collateral.' };
  }
  return { value: 0, formula: '—', note: '' };
}

function EngagementCard({ id, label, sub, checked, onClick }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: checked ? 'primary.main' : 'divider',
        bgcolor: checked ? 'primary.lighter' : 'background.paper',
        height: '100%',
        '&:hover': { borderColor: 'primary.light', bgcolor: checked ? 'primary.lighter' : (t) => alpha(t.palette.primary.main, 0.04) },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ p: 1.75, height: '100%' }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Radio checked={checked} sx={{ p: 0.25, pointerEvents: 'none' }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>{sub}</Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

/**
 * `section` selects which half renders:
 *   'payment' — engagement model, AOV, COD remittance, COD/prepaid split (Stage 1 · Sign-up)
 *   'limit'   — recommended credit limit, sales override, approval upload (Stage 4 · Commercials)
 *   'all'     — both (default; kept so the component still works standalone)
 * Both halves read the same Redux state, so splitting the UI changes nothing about
 * what is stored or saved.
 */
export default function CreditCard({ section = 'all' }) {
  const dispatch = useDispatch();
  const credit = useSelector((s) => s.pricingWizard.credit);
  const masterInfo = useSelector((s) => s.pricingWizard.masterInfo);
  const merchantId = useSelector((s) => s.pricingWizard.merchantId);
  const payment = useSelector((s) => s.pricingWizard.profile.payment);
  const [uploadCreditApproval, { isLoading: uploadingApproval }] = useUploadCreditApprovalMutation();

  const aov = credit.avg_invoice_value || masterInfo.aov || 0;
  const gmv = (masterInfo.monthly_volume || 0) * aov;
  const tier = classifyTier(gmv);
  const codShare = credit.cod_split_pct ?? 50;
  const rec = computeRecommendedLimit({
    engagement: credit.engagement, gmv, credit_period_days: credit.credit_period_days,
    secured_postpaid: credit.secured_postpaid, cod_share_pct: codShare,
  });

  const showCreditLimit = credit.engagement === 'postpaid' || credit.engagement === 'secured_postpaid';
  const showPayment = section === 'all' || section === 'payment';
  const showLimit = (section === 'all' || section === 'limit') && showCreditLimit;

  const onApprovalFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Store locally for preview
    const reader = new FileReader();
    reader.onload = (ev) => dispatch(updateCredit({ approval_file: { name: f.name, type: f.type, size: f.size, dataUrl: ev.target.result } }));
    reader.readAsDataURL(f);
    // Upload to server — s3Key stored in Redux via onQueryStarted
    if (merchantId) {
      try {
        await uploadCreditApproval({ merchantId, file: f }).unwrap();
      } catch { /* non-blocking — preview still works */ }
    }
  };

  const securedExample = (() => {
    const sp = credit.secured_postpaid;
    const start = new Date('2026-05-18');
    const startStr = start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    const end = new Date(start); end.setDate(start.getDate() + (sp.adjust_window_days - 1));
    const endStr = end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    const remit = new Date(end); remit.setDate(remit.getDate() + sp.remit_days);
    const remitStr = remit.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    return `Shipments delivered ${startStr}-${endStr} → COD remittance on ${remitStr} (D+${sp.remit_days}); every ${sp.fee_adjust_every}${sp.fee_adjust_every === 1 ? 'st' : sp.fee_adjust_every === 2 ? 'nd' : sp.fee_adjust_every === 3 ? 'rd' : 'th'} remittance carries the service-fee adjustment for the previous ${sp.adjust_window_days}-day window.`;
  })();

  // Payment-mode lock — per v6.4 fix #6
  const codLocked = payment === 'PREPAID' || payment === 'COD';
  const codLockedValue = payment === 'PREPAID' ? 0 : payment === 'COD' ? 100 : null;

  return (
    <Box sx={{ mb: 3 }}>
      {showPayment && (<>
      <SectionTitle icon="💳" title="Payment & Credit" badge="required" />
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, mb: 1.5 }}>
        Engagement model, average invoice value, COD remittance cycle and the COD/prepaid split.
        {showCreditLimit ? ' The recommended credit limit derived from these is shown on the Commercials step.' : ''}
      </Typography>

      <Grid container spacing={1.5}>
        {ENGAGEMENT_MODELS.map((m) => (
          <Grid item xs={12} md={4} key={m.id}>
            <EngagementCard
              id={m.id} label={m.label} sub={m.sub}
              checked={credit.engagement === m.id}
              onClick={() => dispatch(updateCredit({ engagement: m.id }))}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth size="small" type="number" label="Average invoice value (₹)"
            value={credit.avg_invoice_value || ''}
            placeholder={String(masterInfo.aov || '')}
            onChange={(e) => dispatch(updateCredit({ avg_invoice_value: Number(e.target.value) || 0 }))}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            helperText={masterInfo.aov ? `Defaults to Master Info AOV (₹${masterInfo.aov})` : ''}
          />
        </Grid>

        {credit.engagement === 'postpaid' && (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" type="number" label="Credit exposure (₹)"
                value={credit.credit_exposure || ''}
                onChange={(e) => dispatch(updateCredit({ credit_exposure: Number(e.target.value) || 0 }))}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" select label="Credit period (days)"
                value={credit.credit_period_days}
                onChange={(e) => dispatch(updateCredit({ credit_period_days: Number(e.target.value) }))}
              >
                {[7, 15, 30, 45, 60].map((d) => <MenuItem key={d} value={d}>{`Net ${d}`}</MenuItem>)}
              </TextField>
            </Grid>
          </>
        )}

        {/* COD remittance — GLOBAL: applies to Prepaid, Postpaid and Secured Postpaid */}
        <Grid item xs={12}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
            COD Remittance <Typography component="span" variant="caption" sx={{ color: 'text.secondary', textTransform: 'none' }}>· applies to all engagement models</Typography>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth size="small" select
            label={<span>COD remittance cycle <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>(D+N days)</Box></span>}
            value={credit.secured_postpaid.remit_days}
            onChange={(e) => dispatch(updateSecuredPostpaid({ remit_days: Number(e.target.value) }))}
            helperText="Days between shipment delivery and COD release."
          >
            {[1, 2, 3, 4, 5, 7, 10].map((d) => <MenuItem key={d} value={d}>{`D+${d}`}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth size="small" select label="Remittance frequency / week"
            value={credit.secured_postpaid.remit_freq_per_week}
            onChange={(e) => dispatch(updateSecuredPostpaid({ remit_freq_per_week: Number(e.target.value) }))}
            helperText="How many COD payouts per week."
          >
            <MenuItem value={1}>Weekly (1×)</MenuItem>
            <MenuItem value={2}>Twice / week (2×)</MenuItem>
            <MenuItem value={3}>Thrice / week (3×)</MenuItem>
            <MenuItem value={5}>Daily (Mon-Fri)</MenuItem>
            <MenuItem value={7}>Daily incl. weekends</MenuItem>
          </TextField>
        </Grid>
        {/* Service-fee adjustment only applies to Secured Postpaid — the fee is
            deducted from the COD remittance only in that model. */}
        {credit.engagement === 'secured_postpaid' && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth size="small" select label="Service-fee adjustment every…"
                value={credit.secured_postpaid.fee_adjust_every}
                onChange={(e) => dispatch(updateSecuredPostpaid({ fee_adjust_every: Number(e.target.value) }))}
                helperText="Cadence at which Prozo deducts its fee from a remittance."
              >
                {[1, 2, 3, 4, 5].map((d) => <MenuItem key={d} value={d}>{`Every ${d}${d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} remittance`}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth size="small" type="number" label="Adjustment window (days)"
                value={credit.secured_postpaid.adjust_window_days}
                onChange={(e) => dispatch(updateSecuredPostpaid({ adjust_window_days: Number(e.target.value) || 0 }))}
                helperText="Window of shipments whose fees are settled per adjustment (e.g. 7 = one week of shipments)."
              />
            </Grid>
          </>
        )}
        {credit.engagement === 'secured_postpaid' && (
          <Grid item xs={12}>
            <Alert severity="info" icon={<span>💡</span>} sx={{ fontSize: 12 }}>
              <b>Worked example:</b> {securedExample}
            </Alert>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* COD vs Prepaid split */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
          COD vs Prepaid split
        </Typography>
        {codLocked && (
          <Chip
            size="small"
            label={`Locked to ${codLockedValue}% COD — payment mode is ${payment}`}
            color="warning"
            sx={{ ml: 1, fontSize: 11, height: 20 }}
          />
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mt: 1 }}>
          <Box sx={{ minWidth: 100, fontFamily: 'JetBrains Mono, monospace', color: 'warning.dark' }}>
            COD: <b>{codLocked ? codLockedValue : codShare}%</b>
          </Box>
          <Slider
            value={codLocked ? codLockedValue : codShare}
            disabled={codLocked}
            onChange={(_, v) => dispatch(setCodSplit(v))}
            min={0} max={100} step={5}
            sx={{ flex: 1 }}
          />
          <Box sx={{ minWidth: 110, fontFamily: 'JetBrains Mono, monospace', color: 'success.dark', textAlign: 'right' }}>
            Prepaid: <b>{100 - (codLocked ? codLockedValue : codShare)}%</b>
          </Box>
        </Stack>
        <Box sx={{ mt: 1.25, height: 12, display: 'flex', borderRadius: 1, overflow: 'hidden' }}>
          <Box sx={{ width: `${codLocked ? codLockedValue : codShare}%`, bgcolor: 'warning.main' }} />
          <Box sx={{ width: `${100 - (codLocked ? codLockedValue : codShare)}%`, bgcolor: 'success.main' }} />
        </Box>
      </Box>
      </>)}

      {/* ── Credit Limit (postpaid / secured postpaid only) ── */}
      {showLimit && (
        <Box sx={{ mt: 1 }}>
          <Divider sx={{ my: 2 }} />
          <SectionTitle icon="🏦" title="Credit Limit" badge="postpaid" />
          <Box sx={{ mt: 1.25, p: 2, borderRadius: 1.5, bgcolor: 'background.neutral', border: 1, borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'center' }} divider={<Divider orientation="vertical" flexItem />}>
              <Box sx={{ minWidth: 180 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 700 }}>Tier</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: tier.color }}>{tier.tier}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>GMV ≈ ₹{gmv.toLocaleString('en-IN')}</Typography>
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 700 }}>Recommended limit</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 18 }}>₹{rec.value.toLocaleString('en-IN')}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'JetBrains Mono, monospace' }}>{rec.formula}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <TextField
                  fullWidth size="small" label="Sales Leader override (optional)"
                  value={credit.sales_override}
                  onChange={(e) => dispatch(updateCredit({ sales_override: e.target.value }))}
                  helperText={rec.note}
                />
              </Box>
            </Stack>
          </Box>

          {/* Postpaid approval upload */}
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
              Credit approval (email)
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
              Upload the email approval if the credit limit is beyond the company's standard SOP. PNG or PDF.
            </Typography>
            {credit.approval_file ? (
              <Stack direction="row" alignItems="center" spacing={1.25}
                sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1.5, maxWidth: 460 }}>
                {credit.approval_file.type === 'application/pdf' ? (
                  <Link href={credit.approval_file.dataUrl} target="_blank" rel="noopener" sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                    {credit.approval_file.name} (open PDF)
                  </Link>
                ) : (
                  <>
                    <Box component="img" src={credit.approval_file.dataUrl} alt="approval preview"
                      sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {credit.approval_file.name}
                    </Typography>
                  </>
                )}
                {uploadingApproval
                  ? <CircularProgress size={16} sx={{ mx: 0.5 }} />
                  : <IconButton size="small" onClick={() => dispatch(updateCredit({ approval_file: null, approval_s3Key: null }))}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                }
              </Stack>
            ) : (
              <Button component="label" size="small" variant="outlined"
                startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 15 }} />}
                sx={{ borderRadius: '8px', fontWeight: 600 }}>
                Upload approval
                <input hidden type="file" accept="image/png,application/pdf" onChange={onApprovalFile} />
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
